/**
 * Delegated `data-act` action dispatch (RFC §4, recon-01 constraint 9).
 *
 * ONE delegated listener on the host element reads `data-act` + the `data-*`
 * payload from `ev.target.closest("[data-act]")` and dispatches to the verb's
 * handler. Render stays pure: markup declares affordances (`data-act="bookmark"
 * data-opportunity="urn:…"` on a real <button>/<a>, AX-3); behavior lives here.
 *
 * Semantics pinned by the tests:
 * - The listener is a bubble-phase "click" listener (v1: every affordance is a
 *   click on a real <button>/<a> per AX-3; other trigger events can be added by
 *   RFC amendment).
 * - The payload is the acting node's full `dataset` (camelCased data-* entries,
 *   `act` included), snapshotted at dispatch.
 * - A matched verb stops propagation, so a nested element's action is handled
 *   by the NEAREST host that knows the verb — outer hosts never double-handle.
 * - Unknown verbs and clicks outside any `[data-act]` fall through untouched.
 */

import type { Dispose } from "./viewmodel";

/** Snapshot of the acting node's `dataset` at dispatch time. */
export type ActionPayload = Readonly<Record<string, string | undefined>>;

/** Handler for one `data-act` verb. */
export type ActionHandler = (payload: ActionPayload, ev: Event) => void;

/** Action handlers keyed by `data-act` verb. */
export type ActionMap = Readonly<Record<string, ActionHandler>>;

/**
 * Install the single delegated action listener on `host`.
 * `getActions` is consulted per event so the map may derive from current VM state.
 * Returns the uninstaller.
 */
export function installActionDelegate(
  host: Element,
  getActions: () => ActionMap | undefined
): Dispose {
  const onClick = (ev: Event) => {
    const target = ev.target as Element | null;
    const actEl = target?.closest?.("[data-act]");
    if (!actEl || !(actEl instanceof HTMLElement)) return;
    if (actEl !== host && !host.contains(actEl)) return;

    const verb = actEl.dataset["act"];
    if (!verb) return;

    const handler = getActions()?.[verb];
    if (!handler) return;

    ev.stopPropagation();
    handler({ ...actEl.dataset }, ev);
  };

  host.addEventListener("click", onClick);
  return () => host.removeEventListener("click", onClick);
}
